import { useState, useEffect, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { BaseModal } from '@anyroad/modal';

import {
  TimerContainer,
  TimerText,
  ModalHeader,
  ModalMessage,
  ModalContainer,
  ButtonStyles,
} from './styles';

interface BookingTimerProps {
  sessionExpired: number | string;
  className?: string;
  guideId?: number;
  tourId?: number;
  originUrl?: string;
}

interface TimeRemaining {
  minutes: number;
  seconds: number;
  total: number;
}

interface TimeoutModalProps {
  guideId?: number;
  tourId?: number;
  originUrl?: string;
}

const TimeoutModalContent = ({
  guideId,
  tourId,
  originUrl,
}: TimeoutModalProps) => {
  const handleRedirect = () => {
    const buttonUrl =
      window !== window.top && guideId && tourId
        ? `/i/${guideId}/c/tours/${tourId}`
        : originUrl || '/';

    window.location.href = buttonUrl;
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <FormattedMessage
          defaultMessage="Time limit reached"
          description="Time limit reached"
        />
      </ModalHeader>
      <ModalMessage>
        <FormattedMessage
          defaultMessage="Sorry, your booking had to be released. Please restart your booking."
          description="Sorry, your booking had to be released. Please restart your booking."
        />
      </ModalMessage>
      <ButtonStyles className="brand-bg" onClick={handleRedirect}>
        <FormattedMessage defaultMessage="Go Back" description="Go Back" />
      </ButtonStyles>
    </ModalContainer>
  );
};

const TimeoutModal = BaseModal<TimeoutModalProps>(TimeoutModalContent, {
  padding: '32px',
  width: '600px',
  isCloseButtonHidden: true,
  isBackgroundClickable: false,
});

export const BookingTimer = ({
  sessionExpired,
  className,
  guideId,
  tourId,
  originUrl,
}: BookingTimerProps) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    minutes: 0,
    seconds: 0,
    total: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const previousTimeRef = useRef<number>(0);

  const handleTimerComplete = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const calculateTimeLeft = useCallback(
    (now: number) => {
      let sessionExpiredMs: number;
      if (typeof sessionExpired === 'number') {
        sessionExpiredMs =
          sessionExpired > 1000000000000
            ? sessionExpired
            : sessionExpired * 1000;
      } else {
        sessionExpiredMs = new Date(sessionExpired).getTime();
      }

      return Number.isNaN(sessionExpiredMs)
        ? 0
        : Math.max(0, sessionExpiredMs - now);
    },
    [sessionExpired]
  );

  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();
      const timeLeft = calculateTimeLeft(now);

      const minutes = Math.floor(timeLeft / 60000);
      const seconds = Math.floor((timeLeft % 60000) / 1000);

      const newTimeRemaining = {
        minutes,
        seconds,
        total: timeLeft,
      };

      setTimeRemaining(newTimeRemaining);

      // Check if timer has reached 0 and trigger modal
      if (timeLeft === 0 && previousTimeRef.current > 0) {
        handleTimerComplete();
      }
      previousTimeRef.current = timeLeft;
    };

    updateTimer();

    // Check if timer is already expired on mount
    const now = Date.now();
    const timeLeft = calculateTimeLeft(now);
    if (timeLeft === 0) {
      handleTimerComplete();
    }

    const interval = setInterval(updateTimer, 1000) as NodeJS.Timeout;

    return () => clearInterval(interval);
  }, [sessionExpired, handleTimerComplete, calculateTimeLeft]);

  if (timeRemaining.total <= 0 && !isModalOpen) {
    return null;
  }

  const formatTime = (minutes: number, seconds: number) => {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {timeRemaining.total > 0 && (
        <TimerContainer
          className={className}
          data-testid="mobile-first-booking-timer"
        >
          <TimerText>
            <FormattedMessage
              defaultMessage="We are holding your booking for <strong>{time}</strong> minutes"
              values={{
                time: formatTime(timeRemaining.minutes, timeRemaining.seconds),
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </TimerText>
        </TimerContainer>
      )}

      <TimeoutModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        guideId={guideId}
        tourId={tourId}
        originUrl={originUrl}
      />
    </>
  );
};
